import { LocationType } from '@/entities/location';
import type { ContextGeoSubject } from '@/shared/types/geography.ts';
import { tourFiltersToQuery, useTourFiltersStore } from '@/entities/filter/store/tour';
import { useLoaderStore } from '@/shared/store/useLoaderStore.ts';
import { useExpSelections } from '@/entities/expSelection';
import { isValidMovementType, type TMovementType, useMovementTypes } from '@/entities/movement-type';
import type { IExpSelection } from '@/entities/expSelection/model/expSelection.ts';
import type { ExpSelectionsParams } from '@/entities/expSelection/api/useExpSelection.ts';

interface Props {
  slug: string;
  location: LocationType;
  contextGeoSubject: ContextGeoSubject;
  page: number;
  isExtraSelection: boolean;
}

export const useToursCardsSelection = (props: Props) => {
  const MIN_EXP_AMOUNT = 2;

  const filterStore = useTourFiltersStore();

  const loaderStore = useLoaderStore();

  const isLocation = computed(() => ({
    isRegion: props.location === LocationType.REGION,
    isCity: props.location === LocationType.CITY,
    isCountry: props.location === LocationType.COUNTRY,
  }));

  const buildPageParams = () => ({
    city: props.location === LocationType.CITY ? props.slug : undefined,
    region: props.location === LocationType.REGION ? props.slug : undefined,
    country: props.location === LocationType.COUNTRY ? props.slug : undefined,
  });

  const buildTagSlugs = () => {
    const isCity = isLocation.value.isCity;
    const tagSlugs = filterStore.searchState.tag__slugs;

    return isCity && tagSlugs && tagSlugs !== 'all' ? tagSlugs : '';
  };

  const buildFilterParams = (queryParams: ExpSelectionsParams) => {
    const tagSlugs = buildTagSlugs();
    const hasDateFilter = !!queryParams.start_date || !!queryParams.end_date;

    return {
      ...queryParams,
      date_filter_mode: hasDateFilter ? 'event_date_range' : undefined,
      tag__slugs: tagSlugs,
      bookable: 'true',
    };
  };

  const baseRequestParams = computed((): ExpSelectionsParams => {
    const pageParams = buildPageParams();
    const queryParams = tourFiltersToQuery(filterStore.searchState, pageParams, {});

    return buildFilterParams(queryParams);
  });

  const { selections: baseSelections } = useExpSelections(baseRequestParams);

  const { movementTypesExpCardLabels } = useMovementTypes();

  const selectionsWithClearedMovementType = computed(() => {
    if (!baseSelections.value) {
      return [] as IExpSelection[];
    }

    return baseSelections.value.map((selection) => ({
      ...selection,
      experiences: selection.experiences.map((experience) => ({
        ...experience,
        movement_type:
          experience.movement_type && isValidMovementType(experience.movement_type)
            ? movementTypesExpCardLabels.value?.[experience.movement_type as TMovementType] || ''
            : '',
        isUnderSanctions: experience.isUnderSanctions ?? false,
      })),
    }));
  });

  const isShowTopSelectionBlock = computed(
    () =>
      selectionsWithClearedMovementType.value.some((item) => item.experiences.length > MIN_EXP_AMOUNT) &&
      props.page === 1 &&
      (!filterStore.searchState.tag__slugs || filterStore.searchState.tag__slugs === 'all') &&
      !filterStore.isFiltersApplied,
  );

  const isShowExtraSelectionBlock = computed(
    () =>
      selectionsWithClearedMovementType.value.some((item) => item.experiences.length > MIN_EXP_AMOUNT) &&
      filterStore.searchState.tag__slugs &&
      filterStore.searchState.tag__slugs !== 'all' &&
      !!filterStore.searchTotalCount,
  );

  const isShowBlock = computed(
    () =>
      loaderStore.isFiltersLoading ||
      (props.isExtraSelection ? isShowExtraSelectionBlock.value : isShowTopSelectionBlock.value),
  );

  return {
    isLocation,
    selectionsWithClearedMovementType,
    isShowBlock,
  };
};
